import { formatCount, tt } from '@agos/shared'

/**
 * What each role may do, and the one thing the switches do not say.
 *
 * **FB-103.** Permissions come in families — `fiskal` beside `fiskal_view`, `fiskal_print`,
 * `fiskal_closure`, `fiskal_storno` — and the guard accepts **either**:
 *
 * ```java
 * @RequirePermission({"fiskal", "fiskal_closure"})
 * // …
 * employeePermissions.isAllowed(principal.id(), principal.systemRole(), List.of(requirement.value()))
 * ```
 *
 * `PermissionCatalog` says so in as many words: *"The parent grants the whole page; the children carve
 * it up. A guard that names both accepts either."* So holding the parent grants every child, whether
 * the child's own switch is on or off.
 *
 * Legacy knows this. It declares the relationship and then throws it away:
 *
 * ```tsx
 * // The parent grant still acts as a wildcard — backend `requireAuthOrPermission(parent, sub)`
 * // passes if EITHER is granted.
 * const SUB_OF: Record<string, string> = { fiskal_view: "fiskal", fiskal_storno: "fiskal", … }
 * // …
 * void SUB_OF; void actionLabelFor;
 * ```
 *
 * The hierarchy survives only as two leading spaces and an arrow inside the label text —
 * `"  ↳ Zobraziť"` — and every child renders as an independent switch. **In the restored data 16 of
 * those switches read *off* while the access is granted**, across three of the four editable roles.
 * The worst of them: `waiter` holds `fiskal`, so *Storno* and *Uzávierka* show off while a waiter can
 * void a fiscal receipt and run a closure. *Vrátiť platbu* was a third until `V51` took the parent
 * `online_payments` off the role and handed back `online_payments_view` - BACKEND.md §10A.134.
 *
 * That is not a cosmetic problem on a permissions screen. An administrator who switches *↳ Storno* off
 * to take away that power has not taken it away, and nothing tells them.
 *
 * The rule cuts both ways, and the second direction is latent here rather than live: denying a child
 * while the parent stays granted does nothing, and denying a *parent* while a child is granted leaves
 * the access open. Neither happens in this data — `admin` grants none of the children whose parents
 * `Dispečing` has denied — so it is stated rather than demonstrated.
 */

export type EditableRole = 'admin' | 'cook' | 'waiter' | 'driver'

export interface PermissionsData {
  allPermissions?: string[]
  editableRoles?: string[]
  permissions?: Record<string, string[]>
  defaults?: Record<string, string[]>
}

// ─── the families ────────────────────────────────────────────────────────────

/**
 * The pairs a guard actually names together, lifted from the backend's annotations.
 *
 * `@RequirePermission({"fiskal", "fiskal_storno"})` is what makes the parent a wildcard, and only these
 * twenty-five pairs exist. **The key shape is not enough**: `fiskal_settings` and `attendance_view` look
 * like children and no guard pairs them with their parent, so holding `fiskal` or `attendance` does not
 * grant them. Legacy's hand-written `SUB_OF` lists 43 pairs — eighteen of which no guard honours — and
 * then voids the constant anyway.
 *
 * Checked two ways by the tests: every pair here also agrees with the key shape, and no child is ever
 * guarded on its own.
 */
export const GUARD_PAIRS: Readonly<Record<string, string>> = {
  burger_box_edit: 'burger_box',
  categories_delete: 'categories',
  categories_edit: 'categories',
  cms_edit: 'cms',
  cms_view: 'cms',
  coupons_delete: 'coupons',
  coupons_edit: 'coupons',
  coupons_view: 'coupons',
  delivery_zones_edit: 'delivery_zones',
  employees_view: 'employees',
  fiskal_closure: 'fiskal',
  fiskal_storno: 'fiskal',
  fiskal_view: 'fiskal',
  gifts_delete: 'gifts',
  gifts_edit: 'gifts',
  gifts_view: 'gifts',
  online_payments_refund: 'online_payments',
  online_payments_view: 'online_payments',
  pizza_edit: 'pizza',
  products_delete: 'products',
  products_edit: 'products',
  statistics_view: 'statistics',
  stock_delete: 'stock',
  stock_edit: 'stock',
  stock_view: 'stock',
}

/**
 * The parent whose grant covers this permission, or `null`.
 *
 * Only a guard pair counts. The catalogue is still consulted, so a pair naming something the server
 * does not know about is ignored rather than trusted.
 */
export function parentOf(permission: string, catalogue: ReadonlySet<string>): string | null {
  const parent = GUARD_PAIRS[permission]
  if (parent === undefined || !catalogue.has(parent)) return null
  return parent
}

/**
 * The family a key *looks* like it belongs to, for grouping on the screen.
 *
 * Presentation only: `attendance_view` is shown under `attendance` because that is where somebody looks
 * for it, and the page says plainly that only a guard pair actually grants.
 */
export function displayParentOf(permission: string, catalogue: ReadonlySet<string>): string | null {
  const cut = permission.lastIndexOf('_')
  if (cut <= 0) return null
  const parent = permission.slice(0, cut)
  return catalogue.has(parent) && parent !== permission ? parent : null
}

/** The children a grant of this permission covers. */
export function childrenOf(permission: string, catalogue: readonly string[]): string[] {
  const known = new Set(catalogue)
  return catalogue.filter((candidate) => parentOf(candidate, known) === permission)
}

/** The children shown under this one, whether or not a grant covers them. */
export function displayChildrenOf(permission: string, catalogue: readonly string[]): string[] {
  const known = new Set(catalogue)
  return catalogue.filter((candidate) => displayParentOf(candidate, known) === permission)
}

/**
 * Whether this permission is granted in effect, not merely ticked.
 *
 * The guard is any-of over the family, so a child is held when it is granted **or** its parent is.
 */
export function isEffective(
  permission: string,
  granted: ReadonlySet<string>,
  catalogue: ReadonlySet<string>,
): boolean {
  if (granted.has(permission)) return true
  const parent = parentOf(permission, catalogue)
  return parent !== null && granted.has(parent)
}

export type SwitchState =
  /** Ticked, and held. */
  | 'on'
  /** Not ticked, and not held. */
  | 'off'
  /** Not ticked, but held anyway because the parent is — FB-103. */
  | 'via-parent'

export function switchState(
  permission: string,
  granted: ReadonlySet<string>,
  catalogue: ReadonlySet<string>,
): SwitchState {
  if (granted.has(permission)) return 'on'
  return isEffective(permission, granted, catalogue) ? 'via-parent' : 'off'
}

/** Every child that reads *off* while its parent grants it — the misrepresented switches. */
export function grantedViaParent(
  granted: ReadonlySet<string>,
  catalogue: readonly string[],
): string[] {
  const known = new Set(catalogue)
  return catalogue.filter((permission) => switchState(permission, granted, known) === 'via-parent')
}

/** A denial that the family rule defeats. Latent in this data; stated because it can happen. */
export function defeatedDenials(
  denied: readonly string[],
  granted: ReadonlySet<string>,
  catalogue: readonly string[],
): { permission: string; reachableVia: string[] }[] {
  const known = new Set(catalogue)
  const found: { permission: string; reachableVia: string[] }[] = []

  for (const permission of denied) {
    const viaParent = parentOf(permission, known)
    const reachableVia: string[] = []
    if (viaParent !== null && granted.has(viaParent)) reachableVia.push(viaParent)
    for (const child of childrenOf(permission, catalogue)) {
      if (granted.has(child)) reachableVia.push(child)
    }
    if (reachableVia.length > 0) found.push({ permission, reachableVia })
  }

  return found
}

// ─── the catalogue, grouped ──────────────────────────────────────────────────

export interface PermissionGroup {
  key: string
  heading: string
  /** Parents first, each followed by its own children, in catalogue order. */
  entries: { permission: string; parent: string | null }[]
}

/**
 * Legacy's own headings, over the catalogue's own order.
 *
 * `PermissionCatalog.ALL` is documented as *"the whole vocabulary, in the order the admin screen lists
 * it"*, so the order comes from there and only the headings are local. A permission the headings do not
 * place still appears — legacy's *Iné* group, kept, because a permission nobody can grant is worse than
 * an ugly heading.
 */
const GROUP_OF: { key: string; heading: string; match: (permission: string) => boolean }[] = [
  {
    key: 'operations',
    heading: tt('Prevádzka'),
    match: (permission) =>
      ['dashboard', 'new_order', 'preorders', 'kitchen', 'dispatch', 'restaurant'].includes(permission),
  },
  {
    key: 'order-actions',
    heading: tt('Akcie objednávok'),
    match: (permission) =>
      ['accept_order', 'cancel_order', 'change_order_status', 'return_to_kitchen'].includes(permission),
  },
  {
    key: 'catalogue',
    heading: tt('Katalóg a sklad'),
    match: (permission) =>
      /^(categories|products|stock|recipes|beverage_stock|lunch_menu|burger_box|pizza|gifts)(_|$)/.test(
        permission,
      ),
  },
  {
    key: 'reports',
    heading: tt('Prehľady'),
    match: (permission) =>
      /^(lists|statistics|analytics|summaries|company_management|company_overview)(_|$)/.test(permission),
  },
  {
    key: 'suppliers',
    heading: tt('Dodávatelia a faktúry'),
    match: (permission) =>
      /^(suppliers|supplier_comparison|invoices|outgoing_invoices|recurring_invoices|reorder_drafts|banking)(_|$)/.test(
        permission,
      ),
  },
  {
    key: 'delivery',
    heading: tt('Rozvoz'),
    match: (permission) =>
      /^(delivery_management|delivery_statistics|external_driver|vehicle_fleet|trip_log|delivery_settings|delivery_zones)(_|$)/.test(
        permission,
      ),
  },
  {
    key: 'marketing',
    heading: tt('Marketing'),
    match: (permission) =>
      /^(marketing|loyalty_program|coupons|campaigns)(_|$)/.test(permission),
  },
  {
    key: 'people',
    heading: tt('Ľudia'),
    match: (permission) =>
      /^(employees|attendance|permissions_admin|revenue_handovers)(_|$)/.test(permission),
  },
  {
    key: 'payments',
    heading: tt('Platby a pokladňa'),
    match: (permission) =>
      /^(fiskal|online_payments|qr_payment)(_|$)/.test(permission),
  },
  {
    key: 'settings',
    heading: tt('Nastavenia'),
    match: (permission) =>
      /^(design|opening_hours|cms|system_settings|settings_|hikvision|system_logs|translations_admin|mobile_app)(_|$)?/.test(
        permission,
      ),
  },
]

export function groupCatalogue(catalogue: readonly string[]): PermissionGroup[] {
  const known = new Set(catalogue)
  const placed = new Set<string>()
  const groups: PermissionGroup[] = []

  for (const definition of GROUP_OF) {
    const entries = catalogue
      .filter((permission) => !placed.has(permission) && definition.match(permission))
      .map((permission) => ({ permission, parent: displayParentOf(permission, known) }))
    for (const entry of entries) placed.add(entry.permission)
    if (entries.length > 0) {
      groups.push({ key: definition.key, heading: definition.heading, entries })
    }
  }

  const leftover = catalogue.filter((permission) => !placed.has(permission))
  if (leftover.length > 0) {
    groups.push({
      key: 'other',
      heading: tt('Iné'),
      entries: leftover.map((permission) => ({ permission, parent: displayParentOf(permission, known) })),
    })
  }

  return groups
}

/** Nothing may go missing from the screen — the invariant legacy's hand-written groups can break. */
export function groupsCoverCatalogue(
  groups: PermissionGroup[],
  catalogue: readonly string[],
): boolean {
  const shown = new Set(groups.flatMap((group) => group.entries.map((entry) => entry.permission)))
  return shown.size === new Set(catalogue).size
}

// ─── labels ──────────────────────────────────────────────────────────────────

const ACTION_LABELS: Record<string, string> = {
  view: tt('Zobraziť'),
  edit: tt('Upraviť'),
  delete: tt('Zmazať'),
  refund: tt('Vrátiť platbu'),
  storno: 'Storno',
  closure: tt('Uzávierka'),
  print: tt('Tlačiť doklad'),
  run: tt('Spustiť'),
  email: 'E-mail',
  sms: 'SMS',
  push: 'Push',
  points: tt('Body'),
  export: tt('Export'),
  anonymize: tt('Anonymizovať'),
  deactivate: tt('Deaktivovať'),
  admin: tt('Správa'),
}

/**
 * A child's label is the action it carves out, not the whole key.
 *
 * `actionLabelFor` exists in legacy and is voided beside `SUB_OF`. Used here, so `fiskal_storno` reads
 * *Storno* under *fiskal* rather than as a separate line whose relationship is two spaces of indent.
 */
export function actionLabel(permission: string, parent: string | null): string {
  if (parent === null) return permission
  const action = permission.slice(parent.length + 1)
  return ACTION_LABELS[action] ?? action
}

/**
 * What each permission is called — §9.145, the owner's point 37.
 *
 * <p><b>The measurement the point deserves.</b> The catalogue holds 123 permissions. 54 of them
 * already draw a Slovak action label, because {@code displayParentOf} finds a parent in the key
 * and {@code actionLabel} names what is left. The other <b>69</b> fell through to
 * {@code parent === null ? permission : …} and were titled with the raw database slug —
 * `dashboard`, `new_order`, `return_to_kitchen`. That is the *názvy všelijaké*: which naming
 * system a row got depended on whether its key happened to contain an underscore.
 *
 * <p><b>Two sources, and neither is me.</b> 44 are legacy's own `PERM_GROUPS` labels — the same
 * file §9.107 took the descriptions from, and the field it left behind. The other 24 are the
 * app's own sidebar label for the screen that permission guards, read off `nav-tree.ts` where
 * exactly one entry is gated on it: `outgoing_invoices` is *Vydané faktúry* because that is
 * what the menu already calls the page it opens. Naming a right after the screen it opens is
 * not an invention; naming it out of my own head would have been.
 *
 * <p><b>One permission is still spelled, and that is correct.</b> `translations_admin` has no
 * legacy label and gates no single nav entry, so it keeps its key rather than acquiring a name
 * nobody chose. §9.107's rule, applied the same way: a row must never vanish because nothing
 * has been written for it yet.
 */
const PERMISSION_LABELS: Record<string, string> = {
  accept_order: tt('Prijať novú web objednávku'),
  analytics: tt('Analytika'),
  attendance: tt('Dochádzka'),
  banking: tt('Banka - auto-platby'),
  beverage_stock: tt('Nápojový sklad'),
  burger_box: tt('Kombo BOX'),
  campaigns_edit: tt('Kampane - upraviť'),
  campaigns_run: tt('Kampane - spustiť'),
  campaigns_view: tt('Kampane - zobraziť'),
  cancel_order: tt('Stornovať objednávku'),
  categories: tt('Kategórie'),
  change_order_status: tt('Zmeniť stav objednávky'),
  cms: tt('CMS Stránky'),
  company_management: tt('Sekcia: Správa firmy'),
  company_overview: tt('Prehľad firmy'),
  coupons: tt('Kupóny a zľavy'),
  dashboard: tt('Prehľad (Dashboard)'),
  delivery_management: tt('Sekcia: Správa rozvoz'),
  delivery_settings: tt('Nastavenia rozvozu'),
  delivery_statistics: tt('Rozvoz'),
  delivery_zones: tt('Oblasti doručenia'),
  design: tt('Design stránky'),
  dispatch: tt('Rozvoz - dispečing'),
  employees: tt('Zamestnanci'),
  external_driver: tt('Externý Šofér'),
  fiskal: tt('Pokladňa & uzávierky'),
  fiskal_closures_view: tt('Pokladňa - uzávierky'),
  fiskal_receipts_view: tt('Fiškálne doklady'),
  gifts: tt('Darčeky k objednávke'),
  hikvision: tt('Hikvision zariadenia'),
  invoices: tt('Faktúry'),
  kitchen: tt('Kuchyňa'),
  lists: tt('Zoznamy & Archív'),
  loyalty_program: tt('Vernostný program (nastavenia)'),
  lunch_menu: tt('Obedové menu'),
  marketing: tt('Sekcia: Marketing'),
  mobile_app: tt('Mobilná aplikácia (PWA)'),
  new_order: tt('Nová objednávka'),
  online_payments: tt('Platby online'),
  opening_hours: tt('Otváracie hodiny'),
  outgoing_invoices: tt('Vydané faktúry'),
  permissions_admin: tt('Oprávnenia rolí'),
  pizza: tt('Pizza konfigurátor'),
  preorders: tt('Predobjednávky'),
  products: tt('Produkty'),
  qr_payment: tt('QR Platba (SEPA)'),
  recipes: tt('Recepty'),
  recurring_invoices: tt('Opakované faktúry'),
  reorder_drafts: tt('Návrhy objednávok'),
  restaurant: tt('Reštaurácia (plán sály)'),
  return_to_kitchen: tt('Vrátiť do kuchyne'),
  revenue_handovers: tt('Odovzdanie tržby'),
  settings_backups: tt('Zálohy databázy'),
  settings_email: tt('E-mail nastavenia'),
  settings_general: tt('Všeobecné nastavenia'),
  settings_integrations: tt('Rozvozové platformy'),
  settings_monitoring: tt('Monitoring & Alerty'),
  settings_notifications: tt('Notifikácie & kanály'),
  settings_sms: tt('SMS nastavenia'),
  statistics: tt('Štatistiky'),
  stock: tt('Skladové hospodárstvo'),
  summaries: tt('Sumáre'),
  supplier_comparison: tt('Porovnanie cien'),
  suppliers: tt('Dodávatelia'),
  system_logs: tt('Logy'),
  system_settings: tt('Sekcia: Systémové nastavenia'),
  trip_log: tt('Kniha jázd'),
  vehicle_fleet: tt('Vozový Park'),
}

/**
 * The title of a row: the action for a child, the permission's name for a parent, the key when
 * nothing has been written. Replaces the slug in the title only — the key is still printed
 * under the row, because an administrator comparing this screen to a guard in the source needs
 * it. Naming a thing is not concealing what it is called.
 */
export function permissionLabel(permission: string, parent: string | null): string {
  if (parent !== null) return actionLabel(permission, parent)
  return PERMISSION_LABELS[permission] ?? permission
}

export function roleLabel(role: string): string {
  if (role === 'admin') return tt('Administrátor')
  if (role === 'cook') return tt('Kuchár')
  if (role === 'waiter') return tt('Čašník')
  if (role === 'driver') return tt('Šofér')
  if (role === 'main_admin') return tt('Hlavný administrátor')
  return role
}

// ─── editing a role ──────────────────────────────────────────────────────────

export interface RoleDiff {
  added: string[]
  removed: string[]
  /** Children the change turns on or off *in effect*, without their own switch moving — FB-103. */
  sideEffects: { permission: string; nowHeld: boolean }[]
}

/**
 * What saving would change, including what it changes without being asked.
 *
 * Legacy's diff is added/removed on the ticked set. Turning `fiskal` on also grants four children;
 * turning it off takes them away unless they are ticked themselves. Both belong in the preview.
 */
export function roleDiff(
  before: ReadonlySet<string>,
  after: ReadonlySet<string>,
  catalogue: readonly string[],
): RoleDiff {
  const known = new Set(catalogue)
  const added = [...after].filter((permission) => !before.has(permission)).sort()
  const removed = [...before].filter((permission) => !after.has(permission)).sort()

  const sideEffects: { permission: string; nowHeld: boolean }[] = []
  for (const permission of catalogue) {
    if (added.includes(permission) || removed.includes(permission)) continue
    const was = isEffective(permission, before, known)
    const now = isEffective(permission, after, known)
    if (was !== now) sideEffects.push({ permission, nowHeld: now })
  }

  return { added, removed, sideEffects }
}

export function isDirty(diff: RoleDiff): boolean {
  return diff.added.length > 0 || diff.removed.length > 0
}

/** Only what the catalogue knows is worth counting, or sending. */
export function grantedCount(granted: ReadonlySet<string>, catalogue: readonly string[]): number {
  return catalogue.filter((permission) => granted.has(permission)).length
}

export function effectiveCount(granted: ReadonlySet<string>, catalogue: readonly string[]): number {
  const known = new Set(catalogue)
  return catalogue.filter((permission) => isEffective(permission, granted, known)).length
}

/**
 * The body `PUT /settings/permissions/{role}` wants.
 *
 * Filtered against the catalogue, because a key the server does not know is rejected and returned in
 * `rejected` — better not to send it than to explain it afterwards.
 */
export function rolePayload(
  granted: ReadonlySet<string>,
  catalogue: readonly string[],
): { permissions: string[] } {
  return { permissions: catalogue.filter((permission) => granted.has(permission)) }
}

export function toggled(granted: ReadonlySet<string>, permission: string): Set<string> {
  const next = new Set(granted)
  if (next.has(permission)) next.delete(permission)
  else next.add(permission)
  return next
}

export function withAll(
  granted: ReadonlySet<string>,
  permissions: readonly string[],
  grant: boolean,
): Set<string> {
  const next = new Set(granted)
  for (const permission of permissions) {
    if (grant) next.add(permission)
    else next.delete(permission)
  }
  return next
}

export function matchesSearch(permission: string, label: string, search: string): boolean {
  const needle = search.trim().toLowerCase()
  if (needle === '') return true
  return permission.toLowerCase().includes(needle) || label.toLowerCase().includes(needle)
}

// ─── one employee's overrides ────────────────────────────────────────────────

export interface Override {
  permission?: string
  granted?: boolean
}

export interface EmployeePermissions {
  id?: number
  name?: string
  email?: string | null
  systemRole?: string
  overrides?: Override[]
  rolePermissions?: string[]
  effectivePermissions?: string[]
}

export type OverrideState = 'inherit' | 'grant' | 'deny'

export function overrideState(permission: string, overrides: readonly Override[]): OverrideState {
  const found = overrides.find((override) => override.permission === permission)
  if (found === undefined) return 'inherit'
  return found.granted === true ? 'grant' : 'deny'
}

/**
 * What the employee actually holds, and why.
 *
 * `effectivePermissions` is the server's own answer and is preferred over recomputing — two clients
 * resolving the same rule two ways is how an override appears to work on one screen and not another.
 * The reason is derived only for the label.
 */
export type HeldBecause = 'override' | 'role' | 'parent' | 'no'

export function heldBecause(
  permission: string,
  employee: EmployeePermissions,
  catalogue: ReadonlySet<string>,
): HeldBecause {
  // Whether it is held is the server's answer, always. Recomputing it here would let one screen
  // disagree with another about the same override, and the override rules are exactly where that
  // matters — a denied parent stops granting its children, which no amount of client-side reasoning
  // about the role's list would show.
  if (!(employee.effectivePermissions ?? []).includes(permission)) return 'no'

  // Only *why* is derived, and only for something already known to be held.
  if (overrideState(permission, employee.overrides ?? []) === 'grant') return 'override'
  if ((employee.rolePermissions ?? []).includes(permission)) return 'role'
  void parentOf(permission, catalogue)
  return 'parent'
}

export function cycleOverride(state: OverrideState): OverrideState {
  if (state === 'inherit') return 'grant'
  if (state === 'grant') return 'deny'
  return 'inherit'
}

export function withOverride(
  overrides: readonly Override[],
  permission: string,
  state: OverrideState,
): Override[] {
  const rest = overrides.filter((override) => override.permission !== permission)
  if (state === 'inherit') return rest
  return [...rest, { permission, granted: state === 'grant' }]
}

/**
 * What saving one employee's exceptions would change.
 *
 * The roles tab has had a preview since it was ported; the exceptions tab had none, and it is the tab
 * where a mistake is quieter — a role change is visible to everybody at once, an exception applies to
 * one person and nobody else can see it is there.
 *
 * An override is **three-state**, so the diff is not added/removed. Moving `inherit → deny` and
 * `grant → inherit` both take something away and mean different things: the first says *this person
 * specifically may not*, the second says *whatever the role says*. They are counted apart, because
 * undoing one is not undoing the other.
 */
export interface OverrideDiff {
  granted: string[]
  denied: string[]
  returnedToRole: string[]
}

export function overrideDiff(
  before: readonly Override[],
  after: readonly Override[],
  catalogue: readonly string[],
): OverrideDiff {
  const diff: OverrideDiff = { granted: [], denied: [], returnedToRole: [] }
  for (const permission of catalogue) {
    const was = overrideState(permission, before)
    const now = overrideState(permission, after)
    if (was === now) continue
    if (now === 'grant') diff.granted.push(permission)
    else if (now === 'deny') diff.denied.push(permission)
    else diff.returnedToRole.push(permission)
  }
  return diff
}

export function overridesAreDirty(diff: OverrideDiff): boolean {
  return diff.granted.length + diff.denied.length + diff.returnedToRole.length > 0
}

/** The body `PUT /settings/permissions/employee/{id}` wants. */
export function overridePayload(
  overrides: readonly Override[],
  catalogue: readonly string[],
): { overrides: { permission: string; granted: boolean }[] } {
  const known = new Set(catalogue)
  return {
    overrides: overrides
      .filter((override) => override.permission !== undefined && known.has(override.permission))
      .map((override) => ({
        permission: override.permission as string,
        granted: override.granted === true,
      })),
  }
}

export function overrideCount(employee: EmployeePermissions): number {
  return (employee.overrides ?? []).length
}

export function byOverridesThenName(employees: EmployeePermissions[]): EmployeePermissions[] {
  return [...employees].sort(
    (left, right) =>
      overrideCount(right) - overrideCount(left) ||
      (left.name ?? '').localeCompare(right.name ?? ''),
  )
}

// ─── the audit trail ─────────────────────────────────────────────────────────

export interface AuditEntry {
  id?: number
  actorEmployeeId?: number
  actorName?: string
  targetType?: string
  targetKey?: string
  targetName?: string
  action?: string
  before?: unknown
  after?: unknown
  createdAt?: string | null
}

/** Postgres prints `2026-07-29 08:15:04+00`; `Date.parse` refuses it twice. */
export function parseStamp(value: string | null | undefined): number | null {
  const text = (value ?? '').trim()
  if (text === '') return null
  const parsed = Date.parse(text.replace(' ', 'T').replace(/([+-]\d{2})$/, '$1:00'))
  return Number.isFinite(parsed) ? parsed : null
}

export function actionText(action: string | undefined): string {
  if (action === 'replace_role') return tt('zmenil práva role')
  if (action === 'replace_overrides') return tt('zmenil výnimky zamestnanca')
  return action ?? ''
}

interface OverrideSnapshot {
  overrides?: Override[]
  permissions?: string[]
}

/**
 * What one audit entry changed, as permission keys.
 *
 * The endpoint stores `before` and `after` whole, and legacy prints them as JSON. A reader wants to
 * know which permission moved, so the two snapshots are differenced.
 */
export function auditChange(entry: AuditEntry): { added: string[]; removed: string[] } {
  const keysOf = (snapshot: unknown): Set<string> => {
    const value = (snapshot ?? {}) as OverrideSnapshot
    if (Array.isArray(value.permissions)) return new Set(value.permissions)
    return new Set(
      (value.overrides ?? [])
        .filter((override) => override.permission !== undefined)
        .map((override) => `${override.permission as string}${override.granted === true ? '' : ' ✕'}`),
    )
  }
  const before = keysOf(entry.before)
  const after = keysOf(entry.after)
  return {
    added: [...after].filter((key) => !before.has(key)).sort(),
    removed: [...before].filter((key) => !after.has(key)).sort(),
  }
}

export function byMostRecent(entries: AuditEntry[]): AuditEntry[] {
  return [...entries].sort(
    (left, right) =>
      (parseStamp(right.createdAt) ?? 0) - (parseStamp(left.createdAt) ?? 0) ||
      (right.id ?? 0) - (left.id ?? 0),
  )
}

export { formatCount }

// ─── what each permission opens ──────────────────────────────────────────────

/**
 * Legacy's own sentence for every permission — §9.107, FB-203.
 *
 * <p><b>Ported verbatim.</b> These are the owner's words about their own business, and a wrong
 * description is worse than none: it is a lie with a switch next to it. Legacy draws them under the
 * label on `/permissions` and AGOS drew only the label and the key, so granting a right meant reading
 * a slug and guessing — a security decision taken without the information legacy put beside it.
 *
 * <p><b>114 of them, and the count is exact.</b> Legacy's file holds 129 `desc:` entries: 115
 * non-empty, one of which is a developer note to itself, and 8 deliberately blank on the `↳ Zobraziť`
 * child rows whose parent already carries the sentence.
 *
 * <p><b>A permission with no entry here still draws.</b> The catalogue is AGOS's
 * `PermissionCatalog.ALL`, which has moved on from legacy's — one that appeared since must not vanish
 * from the screen because nobody has written a sentence for it yet.
 */
const PERMISSION_DESCRIPTIONS: Record<string, string> = {
  accept_order: tt('Schválenie čakajúcich objednávok zo stránky.'),
  analytics: tt('Pokročilá analytika tržieb / produktov.'),
  attendance: tt('Plný prístup.'),
  attendance_edit: tt('Zápis a oprava dochádzky.'),
  attendance_view: tt('Iba prezeranie dochádzky.'),
  banking: tt('Bankové prepojenie a párovanie faktúr.'),
  beverage_stock: tt('Samostatná inventúra nápojov.'),
  burger_box: tt('Plný prístup.'),
  campaigns_edit: tt('Aktivácia/deaktivácia kampaní + zmena ich settings (Black Friday, Happy Hours, body, atď.).'),
  campaigns_run: tt('Manuálne spustenie kampane (tlačidlo Spustiť teraz). Bez tohto sa kampaň iba edituje.'),
  campaigns_view: tt('Prezeranie zoznamu kampaní + ich štatistík.'),
  cancel_order: tt('Zrušenie existujúcej objednávky.'),
  categories: tt('Plný prístup (alebo vyber konkrétne akcie nižšie).'),
  categories_delete: tt('Zmazanie kategórií.'),
  categories_edit: tt('Vytvorenie a úprava kategórií.'),
  categories_view: tt('Iba čítanie zoznamu kategórií.'),
  change_order_status: tt('Posun medzi stavmi (nová → varí sa → hotová).'),
  cms: tt('Plný prístup.'),
  cms_edit: tt('Úprava CMS obsahu.'),
  cms_view: tt('Iba čítanie obsahových stránok.'),
  company_management: tt('Skryť / odkryť celú sekciu Správa firmy v menu.'),
  company_overview: tt('Stránka /reports - celkové prehľady.'),
  coupons: tt('Plný prístup k stránke /coupons.'),
  coupons_delete: tt('Zmazanie kupónov.'),
  coupons_edit: tt('Vytvorenie a úprava kupónov.'),
  coupons_view: tt('Iba čítanie zoznamu kupónov.'),
  dashboard: tt('Hlavná nástenka - prehľad objednávok a tržieb.'),
  delivery_management: tt('Skryť / odkryť celú sekciu Správa rozvoz v menu.'),
  delivery_settings: tt('Konfigurácia rozvozu.'),
  delivery_statistics: tt('Štatistiky rozvozu.'),
  delivery_zones: tt('Plný prístup.'),
  delivery_zones_edit: tt('Pridanie / úprava zón.'),
  design: tt('Plný prístup.'),
  design_edit: tt('Úprava vzhľadu + homepage modulov.'),
  dispatch: tt('Prehľad aktívnych rozvozov, priraďovanie šoférov.'),
  employees: tt('Plný prístup.'),
  employees_delete: tt('Odstránenie zamestnanca.'),
  employees_edit: tt('Pridanie / úprava údajov.'),
  employees_view: tt('Iba zoznam zamestnancov.'),
  external_driver: tt('Správa externých šoférov.'),
  fiskal: tt('Plný prístup k pokladni (wildcard).'),
  fiskal_closure: tt('Vytvorenie X / Z uzávierky.'),
  fiskal_closures_view: tt('Prístup k zoznamu uzávierok /fiskal-closures.'),
  fiskal_print: tt('Tlač bežných pokladničných dokladov.'),
  fiskal_receipts_view: tt('Prístup k archívu fiškálnych dokladov /fiskal-receipts.'),
  fiskal_settings: tt('Prístup k stránke /fiskal (konfigurácia).'),
  fiskal_storno: tt('Storno fiškálneho dokladu.'),
  fiskal_view: tt('Čítanie fiškálnych dokladov + uzávierok.'),
  gifts: tt('Plný prístup.'),
  hikvision: tt('Stránka /hikvision.'),
  invoices: tt('Prijaté faktúry.'),
  kitchen: tt('Kuchynský displej s aktívnymi objednávkami.'),
  lists: tt('Plný prístup.'),
  lists_view: tt('Iba čítanie archívu.'),
  loyalty_program: tt('Plný prístup k stránke /loyalty-program - program identity, tiers, body, referral pravidlá.'),
  loyalty_program_edit: tt('Zmena nastavení programu, level prahy, multiplikátory, referral pravidlá.'),
  loyalty_program_view: tt('Iba prezeranie nastavení vernostného programu.'),
  lunch_menu: tt('Plný prístup.'),
  marketing: tt('Skryť / odkryť celú sekciu Marketing v menu.'),
  marketing_customers: tt('Plný prístup k stránke Marketing → Zákazníci.'),
  marketing_customers_anonymize: tt('NEZVRATNÁ akcia - premenuje zákazníka na Removed{N}, vymaže PII a anonymizuje objednávky.'),
  marketing_customers_deactivate: tt('Označenie účtu ako deaktivovaný (zákazník sa nemôže prihlásiť, dáta ostávajú).'),
  marketing_customers_email: tt('Odoslanie jednorazového aj hromadného emailu vybraným zákazníkom (cez Resend).'),
  marketing_customers_export: tt('Export zoznamu zákazníkov (pripravené, momentálne nepoužité).'),
  marketing_customers_points: tt('Hromadné pripisanie / odpisanie vernostných bodov.'),
  marketing_customers_push: tt('Odoslanie web-push notifikácie zákazníkom s prihlásenými zariadeniami.'),
  marketing_customers_sms: tt('Odoslanie jednorazovej aj hromadnej SMS (cez BulkGate).'),
  marketing_customers_view: tt('Prezeranie zoznamu zákazníkov, filtre, otvorenie detailu.'),
  mobile_app: tt('Nastavenia PWA a push notifikácií.'),
  new_order: tt('Manuálne vytvorenie objednávky cez pokladňu.'),
  online_payments: tt('Plný prístup k platobnej bráne.'),
  online_payments_refund: tt('Vystavenie refundácie.'),
  online_payments_view: tt('Iba prezeranie archívu platieb.'),
  opening_hours: tt('Plný prístup.'),
  opening_hours_edit: tt('Zmena prevádzkových hodín.'),
  outgoing_invoices: tt('Vystavené faktúry.'),
  permissions_admin: tt('Prístup k tejto stránke s nastaveniami oprávnení.'),
  pizza: tt('Plný prístup.'),
  preorders: tt('Správa predobjednávok (časenka).'),
  products: tt('Plný prístup (alebo vyber akcie nižšie).'),
  products_delete: tt('Zmazanie produktov.'),
  products_edit: tt('Pridanie a úprava produktov.'),
  products_view: tt('Prezeranie produktov + cien.'),
  qr_payment: tt('Plný prístup.'),
  qr_payment_edit: tt('Pripojenie banky, vytvorenie QR.'),
  qr_payment_view: tt('Iba prezeranie nastavení.'),
  recipes: tt('Receptúry položiek a spotreba skladu.'),
  recurring_invoices: tt('Periodické faktúry / šablóny.'),
  reorder_drafts: tt('Auto-návrhy na dopĺňanie zásob.'),
  restaurant: tt('Vizuálny plán stolov a obsluhy.'),
  return_to_kitchen: tt('Z archívu vrátiť doručenú objednávku späť do kuchyne (odráta ju šoférovi do opätovného doručenia).'),
  revenue_handovers: tt('Evidencia odovzdaní hotovosti.'),
  settings_backups: tt('Stránka /settings-backups.'),
  settings_email: tt('Stránka /settings-email.'),
  settings_general: tt('Stránka /settings-general.'),
  settings_integrations: tt('Stránka /settings-integrations.'),
  settings_monitoring: tt('Stránka /settings-monitoring.'),
  settings_notifications: tt('Stránka /settings-notifications - kedy poslať Email vs SMS vs Push (objednávky, body, kampane).'),
  settings_notifications_edit: tt('Zmena per-event kanálových preferencií (napríklad: body - len push, žiadne SMS).'),
  settings_sms: tt('Stránka /settings-sms.'),
  statistics: tt('Plný prístup.'),
  statistics_view: tt('Iba prezeranie štatistík.'),
  stock: tt('Plný prístup k skladu.'),
  stock_delete: tt('Zmazať skladové položky.'),
  stock_edit: tt('Pridať / upraviť skladové položky.'),
  stock_view: tt('Iba čítanie stavu skladu.'),
  summaries: tt('Plný prístup.'),
  summaries_view: tt('Iba prezeranie sumárov.'),
  supplier_comparison: tt('Porovnanie cien medzi dodávateľmi.'),
  suppliers: tt('Katalóg dodávateľov.'),
  system_logs: tt('Stránka /system-logs.'),
  system_settings: tt('Skryť / odkryť celú sekciu Systémové nastavenia v menu.'),
  trip_log: tt('Záznam jázd.'),
  vehicle_fleet: tt('Evidencia vozidiel.'),
}

export function permissionDescription(permission: string): string | null {
  return PERMISSION_DESCRIPTIONS[permission] ?? null
}


/**
 * The employees a search on the *Výnimky* tab leaves showing — §9.238.
 *
 * **Name, e-mail or role**, which is legacy's own three (`permissions.tsx:856`). The role matters as
 * much as the name: *sofer* finds every driver, and looking for "one of the drivers" is how somebody
 * actually arrives at this screen. An empty query is not a filter — everybody stays.
 *
 * Diacritics are **not** folded, deliberately: legacy does not fold them either, and a search that
 * quietly matched *Sofer* against *Šofér* here while the Role tab's did not would be one screen
 * behaving two ways.
 */
export function matchingEmployees<T extends { name?: string | null; email?: string | null; systemRole?: string | null }>(
  employees: readonly T[],
  search: string,
): T[] {
  const query = search.trim().toLowerCase()
  if (query === '') return [...employees]

  return employees.filter((employee) =>
    [employee.name, employee.email, employee.systemRole].some(
      (field) => typeof field === 'string' && field.toLowerCase().includes(query),
    ),
  )
}
